import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAccount } from '../../context/account_context'
import { useBranch } from '../../context/branch_context'
import { useCoin } from '../../context/coin_context'
import {
    CalculateGain,
    SystemBranchEnum,
    useConfigs,
} from '../../context/configs_context'
import { useLoading } from '../../context/loading_context'
import {
    BarcodeTypeEnum,
    BranchStockType,
    Group,
    IvaEnum,
    Mark,
    Model,
    ProductFilters,
    ProductInfo,
    ProductOrderEnum,
    ProductOrderLabel,
    StockLevelEnum,
    StockLevelLabel,
    SubGroup,
    useProduct,
} from '../../context/product_context'
import { useSnack } from '../../context/snack_context'
import { SupplierInfo } from '../../context/supplier_context'
import { getFileEndpoint, getFileInBase64 } from '../../services/media'
import { getModelsByMarkId } from '../../services/model'
import {
    createProduct,
    CreateProductInterface,
    generateInventory,
    generateRandomBarcode,
    GenerateTotalInventoryOutputInterface,
    getProductById,
    getTotalInventory,
    listProducts,
    updateProduct,
} from '../../services/product'
import { getSubGroupsByGroupId } from '../../services/subgroup'
import { listSuppliers } from '../../services/supplier'
import {
    getDateInDDMMYYYYFormat,
    getDateInIsoFormat,
    getProductStockInBranch,
    maskCurrency,
} from '../../utils/maks'
import { ActionsFooter } from '../ActionsFooter'
import GenericModal from '../GenericModal'
import { GenericTable } from '../GenericTable'
import { ProductBarcodeNoteDialog } from '../ProductBarcodeNoteDialog'
import { ProductDetails } from '../ProductDetails'
import { RegisterProduct } from '../RegisterProduct'
import { StyledPaper } from '../StyledPaper'
import { TotalsByCoinItem, TotalsCardByCoin } from '../TotalsCardByCoin'
import { SubtitleHeader } from '../SubtitleHeader'
import { StringField } from '../StringField'

interface Props {
    returnProducts?(product: ProductInfo[]): void
    detailsButtonText?: string
    localNavigation: boolean
    elevation?: number
    filtersFromProps?: Partial<ProductFilters>
    hideActionButtons?: boolean
    imageScreen?: boolean
    imageScreenProductCb?: (product: ProductInfo, stock: number) => JSX.Element
}

export interface InventoryPrintMode {
    internalcode: boolean
    barcode: boolean
    name: boolean
    observation: boolean
    stock: boolean
    buyPrice: boolean
    salePrice: boolean
    priceList: boolean
    locations: boolean
    iva: boolean
    totalCost: boolean
    image?: boolean
    solicitud?: boolean
    grupo?: boolean
    autoprecio?: boolean
    venderSinStock?: boolean
    date: Date | null
    initialDate: Date | null
    finalDate: Date | null
}

export const ProductAdvancedSearch = ({
    returnProducts,
    detailsButtonText,
    localNavigation,
    elevation,
    filtersFromProps,
    hideActionButtons,
    imageScreen,
    imageScreenProductCb,
}: Props) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const loading = useLoading()
    const snack = useSnack()
    const [detailedProduct, setDetailedProduct] = useState<ProductInfo | null>(
        null
    )
    const timeoutRef = useRef<NodeJS.Timeout>()
    const [showNewProduct, setShowNewProduct] = useState(false)
    const [showEditProduct, setShowEditProduct] = useState(0)
    const { accountInfo } = useAccount()
    const {
        productInfo,
        products,
        setProducts,
        selectedProducts,
        setSelectedProducts,
        filters,
        setFilters,
        search,
        setSearch,
        mountCreateProductRequest,
        priceList,
    } = useProduct()
    const [groups, setGroups] = useState<Group[]>([])
    const [subGroups, setSubGroups] = useState<SubGroup[]>([])
    const [marks, setMarks] = useState<Mark[]>([])
    const [models, setModels] = useState<Model[]>([])
    const [totalInventory, setTotalInventory] =
        useState<GenerateTotalInventoryOutputInterface[]>()
    const { configs } = useConfigs()
    const { coins } = useCoin()
    const { branches } = useBranch()
    const [openProductBarcodeDialog, setOpenProductBarcodeDialog] =
        useState(false)
    const [openDeleteProductsModal, setOpenDeleteProductsModal] =
        useState(false)
    const [openDuplicateProductsModal, setOpenDuplicateProductsModal] =
        useState(false)
    const [openGenericMessageModal, setOpenGenericMessageModal] = useState('')
    const [suppliers, setSuppliers] = useState<SupplierInfo[]>([])
    const [openSetPrintMode, setOpenSetPrintMode] = useState(false)
    const [inventoryFileType, setInventoryFileType] = useState<
        'pdf' | 'csv' | 'txt' | 'legal'
    >('pdf')
    const [inventoryMode, setInventoryMode] = useState<InventoryPrintMode>({
        internalcode: true,
        barcode: false,
        name: true,
        observation: false,
        stock: true,
        buyPrice: false,
        salePrice: false,
        priceList: false,
        locations: false,
        totalCost: false,
        iva: false,
        image: false,
        solicitud: false,
        grupo: false,
        autoprecio: false,
        venderSinStock: false,
        date: null,
        initialDate: null,
        finalDate: null,
    })
    const [onlySelected, setOnlySelected] = useState(false)
    const imageContainerRef = useRef<HTMLDivElement>(null)

    const searchSuppliers = async (value: string) => {
        if (!value) return
        try {
            const res = await listSuppliers(
                {
                    skip: 1,
                    take: 10,
                    search: value,
                },
                coins
            )
            setSuppliers(res.data)
        } catch (error) {}
    }

    const canCreateProduct =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Criar')

    const canUpdateProduct =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Atualizar')

    const canUpdateProductBarcode =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar código de barras')

    const canUpdateProductInternalcode =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar código interno')

    const canUpdateProductImage =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar imagem')

    const canUpdateProductLocalization =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Editar localização')

    const canDeleteProduct =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Deletar')

    const canGenerateInventory =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Gerar inventario')

    const canShowStock =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Produtos:Listar estoque')

    useEffect(() => {
        if (productInfo.groups.length > 0) setGroups(productInfo.groups)
    }, [productInfo.groups])

    useEffect(() => {
        if (productInfo.marks.length > 0) setMarks(productInfo.marks)
    }, [productInfo.marks])

    useEffect(() => {
        if (!accountInfo) return

        if (!localNavigation && canGenerateInventory) loadTotalInventory()
    }, [accountInfo])

    useEffect(() => {
        if (!coins.length) return

        getProducts({ ...filters, ...filtersFromProps }, search)
    }, [coins, JSON.stringify(filtersFromProps)])

    const loadTotalInventory = async () => {
        loading.logicShow()
        try {
            const res = await getTotalInventory()
            setTotalInventory(res)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const loadSubGroups = async (id: number) => {
        loading.logicShow()
        try {
            const subGroups = await getSubGroupsByGroupId(id)
            setSubGroups(subGroups)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const loadModels = async (id: number) => {
        loading.logicShow()
        try {
            const models = await getModelsByMarkId(id)
            setModels(models)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const getProducts = async (
        filters: ProductFilters,
        search: string,
        dontSetEmpty?: boolean,
        returnProducts?: boolean,
        appendProduts?: boolean
    ) => {
        loading.logicShow()
        try {
            const res = await listProducts(
                {
                    skip: filters.page,
                    take: filters.rows,
                    search: search.length ? search : undefined,
                    groups: filters.groups.map((g) => g.id),
                    subgroups: filters.subgroups.map((sg) => sg.id),
                    brands: filters.marks.map((m) => m.id),
                    models: filters.models.map((m) => m.id),
                    id_fornecedor: filters.suppliers.map((s) => s.id as number),
                    stockLevel: filters.stockLevel,
                    id_filial_filter:
                        filters.branches[0]?.stockBranchId ??
                        filters.branches[0]?.id,
                    orderBy: filters.orderBy as any,
                },
                coins
            )

            if (returnProducts) {
                loading.logicHide()
                return res
            }

            if (!dontSetEmpty || res.data.length) {
                setFilters({
                    ...filters,
                    rowsCount:
                        filters.page === 1 ? res.count : filters.rowsCount,
                })
                setProducts(
                    !appendProduts ? res.data : [...products, ...res.data]
                )
                if (!appendProduts) imageContainerRef.current?.scroll(0, 0)
            }
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const inventoryTotals: TotalsByCoinItem[] = useMemo(() => {
        if (!totalInventory?.length) return []
        const map = new Map<
            number,
            {
                coin: any
                rows: TotalsByCoinItem['rows']
                stock: number
                cost: number
            }
        >()
        for (const data of totalInventory) {
            const coin = coins.find((c) => c.id === data.id_moeda)
            if (!coin) continue
            const branch = branches.find((n) => n.id === data.id_filial)
            const label = data.id_filial
                ? branch?.name ?? t('Filial')
                : t('Geral')
            const cur = map.get(coin.id as number) ?? {
                coin,
                rows: [],
                stock: 0,
                cost: 0,
            }
            const stock = Number(data.stock) || 0
            cur.rows.push({
                label,
                value: `${stock} ${data.unidade_medida} — ${t(
                    'Costo total'
                )}: ${coin.symbol} ${maskCurrency(data.totalCost, coin)}`,
            })
            cur.stock += stock
            cur.cost += Number(data.totalCost) || 0
            map.set(coin.id as number, cur)
        }
        return Array.from(map.values()).map((g) => {
            const rows = [...g.rows]
            if (g.rows.length > 1)
                rows.push({
                    label: t('Total'),
                    value: `${g.stock} — ${g.coin.symbol} ${maskCurrency(
                        g.cost,
                        g.coin
                    )}`,
                })
            return { coin: g.coin, rows }
        })
    }, [totalInventory, coins, branches])

    if (showNewProduct) {
        return (
            <RegisterProduct
                isEditing={false}
                isMassEditing={false}
                handleClose={() => setShowNewProduct(false)}
                returnNewProduct={(p: ProductInfo) =>
                    returnProducts && returnProducts([p])
                }
                elevation={elevation}
            />
        )
    }

    if (showEditProduct) {
        return (
            <RegisterProduct
                isEditing={true}
                isMassEditing={false}
                handleClose={() => setShowEditProduct(0)}
                editIdFromProps={showEditProduct}
                returnNewProduct={(p: ProductInfo) =>
                    returnProducts && returnProducts([p])
                }
                elevation={elevation}
            />
        )
    }

    const handleChangeProductActivation = async (
        products: ProductInfo[],
        active: boolean
    ) => {
        const requests = products
            .filter((product) => product.isAtive !== active)
            .map((product) => ({
                id_produto: product.id ?? 0,
                flag_ativo: !product.isAtive ? 1 : 0,
            }))

        if (!requests.length) return

        loading.show()
        try {
            for (const request of requests) {
                await updateProduct(request)
            }
            await getProducts(filters, search)
            setSelectedProducts([])
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleDeleteProducts = async (products: ProductInfo[]) => {
        const requests = products.map((product) => ({
            id_produto: product.id ?? 0,
            flag_deletado: 1,
        }))

        if (!requests.length) return

        loading.show()
        try {
            for (const request of requests) {
                await updateProduct(request)
            }
            await getProducts(filters, search)
            setSelectedProducts([])
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleDuplicateProducts = async (products: ProductInfo[]) => {
        loading.show()
        try {
            const createdProductsCodes: string[] = []

            for (const p of products) {
                const [product, barcode, internalcode, image] =
                    await Promise.all([
                        getProductById(p.id as number, coins),
                        generateRandomBarcode(BarcodeTypeEnum.Barcode),
                        generateRandomBarcode(BarcodeTypeEnum.Internal),
                        p.image?.content
                            ? getFileInBase64(p.image?.content)
                            : null,
                    ])

                const requestInfo = mountCreateProductRequest({
                    ...product,
                    barcodes: [barcode],
                    internalCodes: [internalcode],
                    image: image
                        ? {
                              content: image,
                              file: new File([], 'image.png'),
                              isBase64: true,
                          }
                        : null,
                    groups: [],
                    marks: [],
                    models: [],
                    subGroups: [],
                    isEditing: false,
                    step: 1,
                })

                await createProduct(requestInfo)
                createdProductsCodes.push(internalcode)
            }

            await getProducts(filters, search)
            setSelectedProducts([])
            setOpenGenericMessageModal(
                `Productos duplicados con successo. Códigos: ${createdProductsCodes.join(
                    ', '
                )}.`
            )
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleGenerateInventory = async (
        docType: 'pdf' | 'csv' | 'txt' | 'legal'
    ) => {
        if (onlySelected && !selectedProducts.length) {
            snack.error(
                'Seleccione al menos un producto para generar el inventario'
            )
            return
        }
        loading.logicShow()
        try {
            const selectedFilters: Record<string, string> = onlySelected
                ? { 'Solo productos seleccionados': 'Sí' }
                : {
                      Búsqueda: search || 'Ninguna',
                      Grupos:
                          filters.groups.map((g) => g.name).join(', ') ||
                          'Ningun',
                      Subgrupos:
                          filters.subgroups.map((sg) => sg.name).join(', ') ||
                          'Ningun',
                      Marcas:
                          filters.marks.map((mk) => mk.name).join(', ') ||
                          'Ningun',
                      Modelos:
                          filters.models.map((md) => md.name).join(', ') ||
                          'Ningun',
                      Proveedores:
                          filters.suppliers.map((s) => s.name).join(', ') ||
                          'Ningun',
                      'Nível de stock':
                          typeof filters.stockLevel === 'number'
                              ? StockLevelEnum[filters.stockLevel]
                              : 'Ningun',
                      Sucursal: filters.branches[0]?.name || 'Ninguna',
                      Fecha: inventoryMode.date
                          ? getDateInDDMMYYYYFormat(inventoryMode.date)
                          : 'Actual',
                      'Solo productos seleccionados': 'No',
                  }

            await generateInventory({
                search: search.length ? search : undefined,
                groups: filters.groups.map((g) => g.id),
                subgroups: filters.subgroups.map((sg) => sg.id),
                brands: filters.marks.map((m) => m.id),
                models: filters.models.map((m) => m.id),
                id_fornecedor: filters.suppliers.map((s) => s.id as number),
                stockLevel: filters.stockLevel,
                id_filial_filter:
                    filters.branches[0]?.stockBranchId ??
                    filters.branches[0]?.id,
                inventoryMode: {
                    ...inventoryMode,
                    date: inventoryMode.date
                        ? (getDateInIsoFormat(inventoryMode.date) as any)
                        : null,
                    initialDate: inventoryMode.initialDate
                        ? (getDateInIsoFormat(inventoryMode.initialDate) as any)
                        : null,
                    finalDate: inventoryMode.finalDate
                        ? (getDateInIsoFormat(inventoryMode.finalDate) as any)
                        : null,
                },
                selectedFilters,
                productIds: onlySelected
                    ? selectedProducts.map((p) => p.id as number)
                    : undefined,
                orderBy: filters.orderBy as any,
                docType,
            })
            loading.showDocument()
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const handleDetailedProduct = async (id: number) => {
        loading.logicShow()
        try {
            const product = await getProductById(id, coins)
            const newProducts = [...products]
            const index = newProducts.findIndex((p) => p.id === id)
            if (index > -1) newProducts[index] = product
            setProducts(newProducts)
            setDetailedProduct(product)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const getDisabledInventoryPrintModeModal = () => {
        if (inventoryFileType === 'legal') {
            if (!inventoryMode.initialDate) return true
            if (!inventoryMode.finalDate) return true
            if (
                inventoryMode.initialDate.valueOf() >
                inventoryMode.finalDate.valueOf()
            )
                return true
        }

        return false
    }

    const priceListCols = configs.showPriceListInTable
        ? priceList.map((price) => ({
              name: `Precio ${price}`,
              property: ['salePrice'],
              customRender: (product: ProductInfo) => {
                  const foundPrice = product.namedPrice.find(
                      (p) => p.name === price
                  )

                  const text = foundPrice
                      ? `${product.coin?.symbol} ${maskCurrency(
                            foundPrice.price,
                            product.coin
                        )}`
                      : '---'

                  return (
                      <Stack direction={'column'}>
                          <Tooltip title={text}>
                              <Typography fontSize={'0.875rem'}>
                                  {text}
                              </Typography>
                          </Tooltip>
                      </Stack>
                  )
              },
          }))
        : []

    if (imageScreen) {
        return (
            <Stack
                sx={{
                    position: 'relative',
                    overflowY: 'auto',
                    height: '100%',
                    px: '10px',
                }}
                ref={imageContainerRef}
                onScroll={(e) => {
                    if (
                        e.currentTarget.scrollTop +
                            e.currentTarget.clientHeight >=
                            e.currentTarget.scrollHeight - 5 &&
                        products.length < filters.rowsCount &&
                        !loading.isLoading
                    ) {
                        getProducts(
                            { ...filters, page: filters.page! + 1 },
                            search,
                            true,
                            false,
                            true
                        )
                    }
                }}
            >
                <Stack
                    mb={'15px'}
                    position={'sticky'}
                    top={'0'}
                    sx={{
                        background: 'var(--surface)',
                        p: '10px',
                        zIndex: '100',
                    }}
                >
                    <Typography
                        fontWeight={'bold'}
                        fontSize={'20px'}
                        mb={'5px'}
                    >
                        Seleccione sus produtos
                    </Typography>
                    <StringField
                        label="Buscar"
                        maxLength={100}
                        value={search}
                        setValue={(v) => {
                            setSearch(v)
                            if (v === search) return

                            clearTimeout(timeoutRef.current)
                            timeoutRef.current = setTimeout(() => {
                                getProducts({ ...filters, page: 1 }, v)
                            }, 500)
                        }}
                        sx={{
                            width: '100%',
                        }}
                        size="small"
                    />
                    <Stack
                        direction={'row'}
                        p={'5px'}
                        mt={'5px'}
                        spacing={'10px'}
                        width="100%"
                        sx={{ overflowX: 'auto' }}
                    >
                        {groups.map((group) => {
                            const foundIndex = filters.groups.findIndex(
                                (g) => g.id === group.id
                            )

                            return (
                                <Stack
                                    direction={'row'}
                                    alignItems={'center'}
                                    sx={{
                                        p: '7px 12px',
                                        borderRadius: '12px',
                                        backgroundColor:
                                            'rgb(58, 73, 231, 0.2)',
                                    }}
                                >
                                    <Checkbox
                                        sx={{
                                            p: '0',
                                            '&.Mui-checked': {
                                                color: 'var(--text-primary)',
                                            },
                                        }}
                                        checked={foundIndex >= 0}
                                        onChange={() => {
                                            const newValues = [
                                                ...filters.groups,
                                            ]
                                            if (foundIndex === -1)
                                                newValues.push(group)
                                            else newValues.splice(foundIndex, 1)

                                            setFilters({
                                                ...filters,
                                                groups: newValues,
                                                page: 1,
                                            })
                                            getProducts(
                                                {
                                                    ...filters,
                                                    groups: newValues,
                                                    page: 1,
                                                },
                                                search
                                            )
                                        }}
                                    />
                                    <Typography
                                        sx={{ ml: '5px' }}
                                        fontSize={'14px'}
                                        whiteSpace={'nowrap'}
                                    >
                                        {group.name}
                                    </Typography>
                                </Stack>
                            )
                        })}
                    </Stack>
                </Stack>
                <Grid container spacing={4}>
                    {products.map((product) => {
                        let stock = getProductStockInBranch(
                            product,
                            filters.branches[0] ?? null,
                            (configs.showBranchStock || !accountInfo?.branch) &&
                                configs.systemBranch === SystemBranchEnum.Branch
                        )

                        return (
                            <Grid item xs={12} sm={4}>
                                <Card>
                                    <CardContent>
                                        <Box
                                            component={'img'}
                                            src={
                                                product.image?.content
                                                    ? getFileEndpoint(
                                                          product.image.content
                                                      )
                                                    : '/sem-imagem.png'
                                            }
                                            sx={{ maxHeight: '150px' }}
                                        />
                                        <Typography
                                            fontSize={'14px'}
                                            mt={'5px'}
                                        >
                                            Cod: {product.internalCodes[0]}
                                        </Typography>
                                        <Typography>{product.name}</Typography>

                                        <Typography fontWeight={'bold'}>
                                            {product.coin?.symbol}{' '}
                                            {maskCurrency(
                                                product.salePrice,
                                                product.coin
                                            )}
                                        </Typography>
                                        <Typography fontSize={'14px'}>
                                            {stock > 0 ? (
                                                <>
                                                    <Box
                                                        component={'b'}
                                                        sx={{
                                                            color: 'success.main',
                                                        }}
                                                    >
                                                        {stock}
                                                    </Box>{' '}
                                                    unidades disponíveis!
                                                </>
                                            ) : (
                                                <>Sem estoque disponível.</>
                                            )}
                                        </Typography>
                                        {imageScreenProductCb ? (
                                            imageScreenProductCb(product, stock)
                                        ) : (
                                            <></>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )
                    })}
                </Grid>
            </Stack>
        )
    }

    return (
        <>
            <GenericModal
                open={openSetPrintMode}
                title="Seleccione el modo de impresión"
                textButton="Confirmar"
                handleSubmit={() => {
                    handleGenerateInventory(inventoryFileType)
                    setOpenSetPrintMode(false)
                }}
                handleClose={() => setOpenSetPrintMode(false)}
            >
                {inventoryFileType !== 'legal' ? (
                    <>
                        <Stack>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('Fecha del inventário')}
                                    sx={{
                                        width: '100%',
                                        mt: '10px',
                                    }}
                                    format="dd/MM/yyyy"
                                    value={inventoryMode.date}
                                    onChange={(date) => {
                                        setInventoryMode({
                                            ...inventoryMode,
                                            date,
                                        })
                                    }}
                                    disableFuture
                                />
                                {inventoryMode.date && (
                                    <Button
                                        onClick={() =>
                                            setInventoryMode({
                                                ...inventoryMode,
                                                date: null,
                                            })
                                        }
                                        variant="contained"
                                        color="error"
                                        sx={{ mt: '5px' }}
                                    >
                                        {t('Limpar')}
                                    </Button>
                                )}
                            </LocalizationProvider>
                        </Stack>
                        <Stack direction={'row'} sx={{ mb: '8px' }}>
                            <FormControlLabel
                                label={t('Solo productos seleccionados')}
                                control={
                                    <Checkbox
                                        checked={onlySelected}
                                        onChange={(e) =>
                                            setOnlySelected(e.target.checked)
                                        }
                                    />
                                }
                            />
                        </Stack>
                        <SubtitleHeader sx={{ mt: '10px' }}>
                            {t('Campos a exibir')}
                        </SubtitleHeader>
                        <Stack direction={'row'} flexWrap={'wrap'}>
                            <FormControlLabel
                                label={t('Imagen')}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.image}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                image: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Código interno')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.internalcode}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                internalcode: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Código de barras')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.barcode}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                barcode: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Nombre')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.name}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                name: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Observación')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.observation}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                observation: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Solicitud')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.solicitud}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                solicitud: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Grupo')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.grupo}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                grupo: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Stock')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.stock}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                stock: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Precio de compra')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.buyPrice}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                buyPrice: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Precio de venta')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.salePrice}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                salePrice: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Lista de precios')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.priceList}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                priceList: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Localizaciónes')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.locations}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                locations: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('IVA')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.iva}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                iva: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Costo total')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.totalCost}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                totalCost: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Autoprecio')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.autoprecio}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                autoprecio: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                label={t('Vender sin stock')}
                                sx={{}}
                                control={
                                    <Checkbox
                                        checked={inventoryMode.venderSinStock}
                                        onChange={(e) => {
                                            setInventoryMode({
                                                ...inventoryMode,
                                                venderSinStock: e.target.checked,
                                            })
                                        }}
                                    />
                                }
                            />
                        </Stack>
                    </>
                ) : (
                    <Stack direction={'row'} spacing={2} mt={'5px'}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('Fecha inicial')}
                                sx={{
                                    width: '50%',
                                }}
                                format="dd/MM/yyyy"
                                value={inventoryMode.initialDate}
                                onChange={(date) => {
                                    setInventoryMode({
                                        ...inventoryMode,
                                        initialDate: date,
                                    })
                                }}
                                disableFuture
                            />
                            <DatePicker
                                label={t('Fecha final')}
                                sx={{
                                    width: '50%',
                                }}
                                format="dd/MM/yyyy"
                                value={inventoryMode.finalDate}
                                onChange={(date) => {
                                    setInventoryMode({
                                        ...inventoryMode,
                                        finalDate: date,
                                    })
                                }}
                                disableFuture
                            />
                        </LocalizationProvider>
                    </Stack>
                )}
            </GenericModal>
            <GenericModal
                open={openDeleteProductsModal}
                title="Deletar productos"
                textButton="Confirmar"
                handleSubmit={() => {
                    handleDeleteProducts(selectedProducts)
                    setOpenDeleteProductsModal(false)
                }}
                handleClose={setOpenDeleteProductsModal}
                handleClickSecondaryButton={() =>
                    setOpenDeleteProductsModal(false)
                }
            >
                <Box width={'350px'} textAlign={'left'}>
                    {t(
                        '¿Estás seguro de que deseas deletar los productos? Esta acción no se puede revertir.'
                    )}
                </Box>
            </GenericModal>
            <GenericModal
                open={openDuplicateProductsModal}
                title="Duplicar productos"
                textButton="Confirmar"
                handleSubmit={() => {
                    handleDuplicateProducts(selectedProducts)
                    setOpenDuplicateProductsModal(false)
                }}
                handleClose={setOpenDuplicateProductsModal}
                handleClickSecondaryButton={() =>
                    setOpenDuplicateProductsModal(false)
                }
            >
                <Box width={'350px'} textAlign={'left'}>
                    {t(
                        '¿Estás seguro de que deseas duplicar los productos? Esta acción no se puede revertir.'
                    )}
                </Box>
            </GenericModal>
            <GenericModal
                open={!!openGenericMessageModal}
                title="Duplicar productos"
                textButton="Confirmar"
                handleSubmit={() => {
                    setOpenGenericMessageModal('')
                }}
                handleClose={() => setOpenGenericMessageModal('')}
            >
                <Box width={'350px'} textAlign={'left'}>
                    {t(openGenericMessageModal)}
                </Box>
            </GenericModal>
            {openProductBarcodeDialog && (
                <ProductBarcodeNoteDialog
                    products={selectedProducts}
                    handleClose={() => setOpenProductBarcodeDialog(false)}
                />
            )}
            {detailedProduct && (
                <>
                    <StyledPaper elevation={elevation}>
                        <ProductDetails product={detailedProduct} />
                    </StyledPaper>
                    <ActionsFooter
                        cancelLabel="Volver"
                        okLabel={detailsButtonText ?? ''}
                        cancelClick={() => setDetailedProduct(null)}
                        okClick={() => {
                            returnProducts && returnProducts([detailedProduct])
                        }}
                    />
                </>
            )}
            <Box sx={{ display: !detailedProduct ? 'block' : 'none' }}>
                <GenericTable
                    withoutPaper={localNavigation}
                    title="c22e9e07-b390-490d-96cf-dd6004ef5a79"
                    filters={[
                        {
                            name: 'Grupo',
                            labelField: 'name',
                            valueField: 'id',
                            values: groups,
                            selectedValues: filters.groups,
                        },
                        {
                            name: 'Subgrupo',
                            labelField: 'name',
                            valueField: 'id',
                            values: subGroups,
                            selectedValues: filters.subgroups,
                        },
                        {
                            name: 'Marca',
                            labelField: 'name',
                            valueField: 'id',
                            values: marks,
                            selectedValues: filters.marks,
                        },
                        {
                            name: 'Modelo',
                            labelField: 'name',
                            valueField: 'id',
                            values: models,
                            selectedValues: filters.models,
                        },
                        {
                            name: 'Proveedor',
                            labelField: 'name',
                            valueField: 'id',
                            values: suppliers,
                            selectedValues: filters.suppliers,
                            autoComplete: {
                                cb: (v) => {
                                    clearTimeout(timeoutRef.current)
                                    timeoutRef.current = setTimeout(() => {
                                        searchSuppliers(v)
                                    }, 500)
                                },
                            },
                        },
                        {
                            name: 'Nível de estoque',
                            labelField: 'name',
                            valueField: 'id',
                            values: Object.keys(StockLevelEnum)
                                .filter((v) => !isNaN(Number(v)))
                                .map((v) => ({
                                    id: Number(v),
                                    name: StockLevelLabel[Number(v)],
                                })),
                            selectedValues:
                                filters.stockLevel !== null
                                    ? [
                                          {
                                              name: StockLevelLabel[
                                                  filters.stockLevel
                                              ],
                                          },
                                      ]
                                    : [],
                        },
                        {
                            name: 'Ordenar por',
                            labelField: 'name',
                            valueField: 'id',
                            values: Object.values(ProductOrderEnum).map(
                                (v) => ({
                                    id: v,
                                    name: ProductOrderLabel[v],
                                })
                            ),
                            selectedValues:
                                filters.orderBy !== null
                                    ? [
                                          {
                                              name: ProductOrderLabel[
                                                  filters.orderBy
                                              ],
                                          },
                                      ]
                                    : [],
                        },
                        {
                            name: 'Sucursal',
                            labelField: 'name',
                            valueField: 'id',
                            values: branches,
                            selectedValues: filters.branches,
                            hideSelection:
                                (!configs.showBranchStock &&
                                    !!accountInfo?.branch) ||
                                configs.systemBranch !==
                                    SystemBranchEnum.Branch,
                        },
                    ]}
                    search={search}
                    handleChangeSearch={(v) => {
                        setSearch(v)
                        if (v === search) return

                        clearTimeout(timeoutRef.current)
                        timeoutRef.current = setTimeout(() => {
                            getProducts({ ...filters, page: 1 }, v)
                        }, 500)
                    }}
                    handleChangeSelectedFilter={(selectedFilter) => {
                        if (selectedFilter.name === 'Grupo') {
                            const index = filters.groups.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.groups]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                groups: newValues,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    groups: newValues,
                                    page: 1,
                                },
                                search
                            )
                            if (selectedFilter.action === 'add')
                                loadSubGroups(selectedFilter.value?.id)
                        }
                        if (selectedFilter.name === 'Subgrupo') {
                            const index = filters.subgroups.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.subgroups]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                subgroups: newValues,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    subgroups: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Marca') {
                            const index = filters.marks.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.marks]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                marks: newValues,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    marks: newValues,
                                    page: 1,
                                },
                                search
                            )
                            if (selectedFilter.action === 'add')
                                loadModels(selectedFilter.value?.id)
                        }
                        if (selectedFilter.name === 'Modelo') {
                            const index = filters.models.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.models]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                models: newValues,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    models: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Proveedor') {
                            const index = filters.suppliers.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.suppliers]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                suppliers: newValues,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    suppliers: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Nível de estoque') {
                            setFilters({
                                ...filters,
                                stockLevel:
                                    selectedFilter.action === 'add'
                                        ? selectedFilter.value?.id
                                        : null,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    stockLevel:
                                        selectedFilter.action === 'add'
                                            ? selectedFilter.value?.id
                                            : null,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Ordenar por') {
                            setFilters({
                                ...filters,
                                orderBy:
                                    selectedFilter.action === 'add'
                                        ? selectedFilter.value?.id
                                        : null,
                                page: 1,
                            })
                            getProducts(
                                {
                                    ...filters,
                                    orderBy:
                                        selectedFilter.action === 'add'
                                            ? selectedFilter.value?.id
                                            : null,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Sucursal') {
                            setFilters({
                                ...filters,
                                branches:
                                    selectedFilter.action === 'add'
                                        ? [selectedFilter.value]
                                        : [],
                            })
                            getProducts(
                                {
                                    ...filters,
                                    branches:
                                        selectedFilter.action === 'add'
                                            ? [selectedFilter.value]
                                            : [],
                                },
                                search
                            )
                        }
                    }}
                    handleChangeSelectedValues={(values) => {
                        setSelectedProducts(
                            values.map((value) => {
                                const foundValue = selectedProducts.find(
                                    (sv) => sv.id === value
                                )
                                if (foundValue) return foundValue
                                else
                                    return products.filter(
                                        (v) => v.id === value
                                    )[0]
                            })
                        )
                    }}
                    selectedValues={selectedProducts.map((p) => p.id as number)}
                    identifier="id"
                    values={products}
                    pagination={{
                        page: filters.page!,
                        rows: filters.rows!,
                        rowsCount: filters.rowsCount,
                        handleChangePagination: (page, rows) => {
                            getProducts(
                                { ...filters, page, rows },
                                search,
                                true
                            )
                        },
                    }}
                    columns={[
                        {
                            name: 'Nombre',
                            property: ['name'],
                            flex: 2,
                            customRender: (product: ProductInfo, value) => {
                                return (
                                    <Tooltip
                                        title={
                                            product.image?.content ? (
                                                <Box
                                                    component="img"
                                                    src={
                                                        product.image?.content
                                                            ? getFileEndpoint(
                                                                  product.image
                                                                      .content
                                                              )
                                                            : value
                                                    }
                                                    maxWidth={'250px'}
                                                />
                                            ) : (
                                                value
                                            )
                                        }
                                    >
                                        <Typography fontSize={'0.875rem'}>
                                            {value}
                                        </Typography>
                                    </Tooltip>
                                )
                            },
                        },
                        {
                            name: 'Observación',
                            property: ['observation'],
                            parser: (value) => value || '---',
                        },
                        {
                            name: 'Solicitud',
                            property: ['aplication'],
                            parser: (value) => value || '---',
                        },
                        {
                            name: 'Códigos de barras',
                            property: ['barcodes'],
                            parser: (value) => value.join(', '),
                            hideXs: true,
                        },
                        {
                            name: 'Códigos internos',
                            property: ['internalCodes'],
                            parser: (value) => value.join(', ') || '---',
                            hideXs: true,
                        },
                        {
                            name: 'Precio compra',
                            property: ['buyPrice'],
                            customRender: (product: ProductInfo, value) => {
                                const finalCost =
                                    product.avgBuyPrice &&
                                    configs.gainCalculationType ===
                                        CalculateGain.AveragePrice
                                        ? product.avgBuyPrice +
                                          product.avgCifPrice
                                        : product.buyPrice + product.cifPrice

                                const text = `${product.coin
                                    ?.symbol} ${maskCurrency(
                                    finalCost,
                                    product.coin
                                )}`
                                const porcText = `Beneficio estimado: ${product.porcentGain}%`

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography fontSize={'0.875rem'}>
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={porcText}>
                                            <Typography
                                                fontSize={'0.75rem'}
                                                color={'#71797E'}
                                            >
                                                {porcText}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                            hideXs: true,
                        },
                        {
                            name: 'Precio venda',
                            property: ['salePrice'],
                            customRender: (product: ProductInfo, value) => {
                                if (!value) return <></>
                                const text = `${product.coin
                                    ?.symbol} ${maskCurrency(
                                    value,
                                    product.coin
                                )}`

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography fontSize={'0.875rem'}>
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        ...priceListCols,
                        {
                            name: 'Stock',
                            property: ['stock'],
                            customRender: (
                                product: ProductInfo,
                                value: any
                            ) => {
                                if (!canShowStock)
                                    return (
                                        <Tooltip title={'---'}>
                                            <Typography fontSize={'0.875rem'}>
                                                ---
                                            </Typography>
                                        </Tooltip>
                                    )

                                if (
                                    (configs.showBranchStock ||
                                        !accountInfo?.branch) &&
                                    configs.systemBranch ===
                                        SystemBranchEnum.Branch
                                ) {
                                    value = getProductStockInBranch(
                                        product,
                                        filters.branches[0] ?? null,
                                        true
                                    )
                                }

                                const text = `${value} ${product.unitType}`
                                let subtext = ''
                                let color = ''

                                if (
                                    product.minStock &&
                                    value < product.minStock
                                ) {
                                    color = 'error'
                                    subtext = `${product.minStock - value} ${
                                        product.unitType
                                    } ${t('abajo del mínimo')}`
                                } else if (
                                    product.idealStock &&
                                    value < product.idealStock
                                ) {
                                    color = 'warning.main'
                                    subtext = `${product.idealStock - value} ${
                                        product.unitType
                                    } ${t('abajo del ideal')}`
                                } else {
                                    color = 'success.main'
                                    subtext = t(`Arriba del ideal`)
                                }

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography
                                                fontSize={'0.875rem'}
                                                color={color}
                                                fontWeight={'bold'}
                                            >
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={subtext}>
                                            <Typography fontSize={'0.7rem'}>
                                                {subtext}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Stock reservado',
                            property: ['stock'],
                            customRender: (
                                product: ProductInfo,
                                value: any
                            ) => {
                                if (!canShowStock)
                                    return (
                                        <Tooltip title={'---'}>
                                            <Typography fontSize={'0.875rem'}>
                                                ---
                                            </Typography>
                                        </Tooltip>
                                    )

                                let reservedStock = 0

                                if (
                                    (configs.showBranchStock ||
                                        !accountInfo?.branch) &&
                                    configs.systemBranch ===
                                        SystemBranchEnum.Branch &&
                                    filters.branches.length
                                ) {
                                    const stockBranchId =
                                        filters.branches[0]?.stockBranchId ??
                                        filters.branches[0].id

                                    reservedStock =
                                        product.branchStocks?.find(
                                            (b) =>
                                                b.branchId === stockBranchId &&
                                                b.type ===
                                                    BranchStockType.Reserved
                                        )?.stock ?? 0
                                }

                                const text = `${reservedStock} ${product.unitType}`

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography fontSize={'0.875rem'}>
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Localizaciónes',
                            property: ['localizations'],
                            parser: (
                                value: {
                                    value: string
                                    branchId?: number
                                }[]
                            ) =>
                                value
                                    ?.filter((v) =>
                                        filters.branches.length
                                            ? filters.branches.some(
                                                  (b) => b.id === v.branchId
                                              )
                                            : true
                                    )
                                    .map((v) => v.value)
                                    .join(', ') || '---',
                            defaultHidden: true,
                        },
                        {
                            name: 'Activo',
                            property: ['isAtive'],
                            customRender: (product: ProductInfo, value) => {
                                const text = t(value ? 'Sí' : 'No')
                                return (
                                    <Tooltip title={text}>
                                        <Typography
                                            fontSize={'0.875rem'}
                                            color={
                                                value
                                                    ? 'success.main'
                                                    : 'error.main'
                                            }
                                        >
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                )
                            },
                            hideXs: true,
                        },
                    ]}
                    actions={[
                        {
                            name: 'Relatórios',
                            color: 'success',
                            hide:
                                !canGenerateInventory ||
                                localNavigation ||
                                hideActionButtons,
                            dropdownActions: [
                                {
                                    name: 'Generar PDF',
                                    color: 'secondary',
                                },
                                {
                                    name: 'Generar Excel',
                                    color: 'secondary',
                                },
                                {
                                    name: 'Generar TXT',
                                    color: 'secondary',
                                },
                                {
                                    name: 'Generar Relatório Legal',
                                    color: 'secondary',
                                },
                            ],
                        },
                        {
                            name: 'Acciones',
                            color: 'error',
                            hide: hideActionButtons,
                            dropdownActions: [
                                {
                                    name: 'Imprimir código',
                                    color: 'secondary',
                                },
                                {
                                    name: 'Editar',
                                    color: 'secondary',
                                    hide:
                                        !canUpdateProduct &&
                                        !canUpdateProductBarcode &&
                                        !canUpdateProductInternalcode &&
                                        !canUpdateProductImage &&
                                        !canUpdateProductLocalization,
                                },
                                {
                                    name: 'Activar',
                                    color: 'secondary',
                                    hide: !canUpdateProduct,
                                },
                                {
                                    name: 'Desactivar',
                                    color: 'secondary',
                                    hide: !canUpdateProduct,
                                },
                                {
                                    name: 'Duplicar',
                                    color: 'secondary',
                                    hide: !canCreateProduct,
                                },
                                {
                                    name: 'Deletar',
                                    color: 'secondary',
                                    hide: !canDeleteProduct,
                                },
                            ],
                        },
                        {
                            name: 'Nuevo',
                            color: 'primary',
                            hide: !canCreateProduct || hideActionButtons,
                        },
                        {
                            name: 'Seleccionar',
                            color: 'success',
                            dropdownActions: [
                                {
                                    name: 'Apenas marcados',
                                    color: 'secondary',
                                },
                                {
                                    name: 'Todos con estes filtros',
                                    color: 'secondary',
                                },
                            ],
                            hide: !localNavigation || hideActionButtons,
                        },
                    ]}
                    handleExecuteAction={(action) => {
                        if (action === 'Generar PDF') {
                            setInventoryFileType('pdf')
                            setOpenSetPrintMode(true)
                        }
                        if (action === 'Generar Excel') {
                            setInventoryFileType('csv')
                            setOpenSetPrintMode(true)
                        }
                        if (action === 'Generar TXT') {
                            setInventoryFileType('txt')
                            setOpenSetPrintMode(true)
                        }
                        if (action === 'Generar Relatório Legal') {
                            setInventoryFileType('legal')
                            setOpenSetPrintMode(true)
                        }
                        if (action === 'Imprimir código') {
                            if (selectedProducts.length)
                                setOpenProductBarcodeDialog(true)
                        }
                        if (action === 'Editar') {
                            if (selectedProducts.length)
                                !localNavigation
                                    ? navigate(`edit/${selectedProducts[0].id}`)
                                    : setShowEditProduct(
                                          selectedProducts[0].id ?? 0
                                      )
                        }
                        if (action === 'Activar') {
                            handleChangeProductActivation(
                                selectedProducts,
                                true
                            )
                        }
                        if (action === 'Desactivar') {
                            handleChangeProductActivation(
                                selectedProducts,
                                false
                            )
                        }
                        if (action === 'Duplicar') {
                            if (selectedProducts.length)
                                setOpenDuplicateProductsModal(true)
                        }
                        if (action === 'Deletar') {
                            setOpenDeleteProductsModal(true)
                        }
                        if (action === 'Nuevo') {
                            if (localNavigation) setShowNewProduct(true)
                            else navigate('new')
                        }
                        if (action === 'Apenas marcados') {
                            if (selectedProducts.length && returnProducts) {
                                returnProducts(selectedProducts)
                                setSelectedProducts([])
                            }
                        }
                        if (action === 'Todos con estes filtros') {
                            if (returnProducts) {
                                getProducts(
                                    {
                                        ...filters,
                                        page: undefined,
                                        rows: undefined,
                                    },
                                    search,
                                    false,
                                    true
                                ).then((products) => {
                                    if (products) returnProducts(products?.data)
                                })
                            }
                        }
                    }}
                    handleClickDetails={(id) => {
                        const product =
                            products.find((p) => p.id === id) ?? null
                        if (!product) return
                        handleDetailedProduct(product.id as number)
                    }}
                />
                {!localNavigation && canGenerateInventory && (
                    <TotalsCardByCoin
                        title={t('Inventário total')}
                        totals={inventoryTotals}
                    />
                )}
            </Box>
        </>
    )
}
